import { Ajuda } from '../../design/Ajuda';
import { useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { salvarExcecaoDeCaptura } from '../../giro';
import type { ConfigDoGiro } from '../../giro';

/**
 * EXCEÇÃO DE VENDAS DO GIRO (dono, 28/09): "só nessa terça 29/09 puxa 26, 27 e 28". Num dia
 * marcado, o Giro pega as vendas desde a data escolhida até a véspera; nos outros dias vale a
 * regra de sempre (ontem; segunda = sábado e domingo). Versão nova da configuração, com motivo.
 */

const br = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;

export function ExcecaoDeCaptura({
  sessao,
  atual,
  aoSalvar,
}: {
  sessao: Sessao;
  atual: ConfigDoGiro;
  aoSalvar: () => void;
}) {
  const [dia, setDia] = useState('');
  const [desde, setDesde] = useState('');
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);
  const excecoes = Object.entries(atual.excecoes_captura ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  const gravar = async (d: string, de: string | null, m: string) => {
    if (salvando) return;
    setSalvando(true);
    setAviso(null);
    const r = await salvarExcecaoDeCaptura(sessao, d, de, m);
    setSalvando(false);
    if (r.tipo === 'confirmada') {
      setDia('');
      setDesde('');
      setMotivo('');
      setAviso({ tom: 'ok', texto: `Salvo (versão ${String(r.dados.versao)}).` });
      aoSalvar();
    } else
      setAviso({
        tom: 'erro',
        texto:
          r.tipo === 'recusada'
            ? r.mensagem
            : 'Não deu para confirmar se salvou. Recarregue a página e confira.',
      });
  };

  return (
    <div className="cv-cartao gc-cartao gc-horarios">
      <h2>
        Exceção de vendas do Giro
        <Ajuda rotulo="a exceção de vendas">
          Para quando um dia ficou sem Giro (feriado, loja fechada): o Giro do dia escolhido pega as
          vendas de vários dias de uma vez, desde a data marcada. Vale só para aquele dia.
        </Ajuda>
      </h2>
      <p className="gc-nota">
        Regra de sempre: o Giro pega as vendas de <b>ontem</b>; na <b>segunda</b>, sábado e domingo.
        Aqui você abre uma exceção para <b>um dia</b>: o Giro daquele dia pega as vendas desde a
        data escolhida até a véspera.
      </p>
      {excecoes.length > 0 && (
        <ul className="gc-horarios-lista">
          {excecoes.map(([d, de]) => (
            <li key={d}>
              <div className="gc-horarios-linha">
                <b>
                  Giro de {br(d)}: vendas de {br(de)} até a véspera
                </b>
                <button
                  type="button"
                  className="cv-botao"
                  disabled={salvando}
                  onClick={() => void gravar(d, null, `Tirar a exceção do Giro de ${br(d)}`)}
                >
                  Tirar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="gc-linha">
        <label className="gc-campo">
          <span>Dia do Giro</span>
          <input type="date" value={dia} onChange={(e) => setDia(e.target.value)} />
        </label>
        <label className="gc-campo">
          <span>Pegar vendas desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
      </div>
      <label className="gc-campo">
        <span>
          Motivo <b>obrigatório</b>
        </span>
        <textarea
          rows={2}
          maxLength={500}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ex.: sem Giro na segunda; terça pega sábado, domingo e segunda"
        />
      </label>
      <div className="gc-acoes">
        <button
          type="button"
          className="cv-botao cv-botao-primario"
          disabled={!dia || !desde || motivo.trim().length < 5 || salvando}
          onClick={() => void gravar(dia, desde, motivo)}
        >
          <Icone nome="check" tamanho={14} traco={1.8} />
          {salvando ? 'Salvando…' : 'Salvar exceção'}
        </button>
      </div>
      {aviso && (
        <p className={aviso.tom === 'ok' ? 'gc-ok' : 'gc-erro'} role="status">
          {aviso.texto}
        </p>
      )}
    </div>
  );
}
